"use client";
import { type ChatAIUploadInput } from "@chat-ai/_utils/use-chat-ai-file-upload";
import { type ImageAIUploadInput } from "@image-ai/_utils/use-image-ai-file-upload";
import { uploadFile } from "@shared-utils/blob/block-upload-client";
import { safeAwait } from "@shared-utils/misc";
import { type ISearchUploadInput } from "@isearch/_utils/use-isearch-file-upload";
import { type FileType } from "@shared-database/schema";
import { type TranslateAIUploadInput } from "@translate-ai/_utils/use-translate-ai-file-upload";
import _ from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { type TrainingAiUploadInput } from "@training-ai/_utils/use-training-ai-file-upload";
import { SUCCESSFUL_FILE_STATUSES } from "@shared-database/enums";
import { type WorkerJobStatusEnumType } from "@shared-database/schema";
import type { SharedUploadInput } from "@shared-utils/use-shared-file-upload";
import { logFrontendError } from "@shared-utils/leo-logger";
import { PDF_FILE_MIME_TYPE } from "@shared-server/api/utils/file/constants";
import type { MarketResearchUploadInput } from "@market-research/_utils/use-market-research-file-upload";

// Concurrent uploads, including metadata creation and finalization.
const MAX_UPLOAD = 3;
const PROCESSING_POLL_INITIAL_DELAY_MS = 3000;
const PROCESSING_POLL_MAX_DELAY_MS = 60_000;
const PROCESSING_POLL_BACKOFF_FACTOR = 2;
const STATUS_CHECK_FAILED_POLL_MAX_ATTEMPTS = 10;
const RECOVERABLE_PROCESSING_FAILED_STATUS = "PROCESSING_FAILED";

// App and input type used for mapping with handlers
export type UploadType =
  | {
      app: "CHAT_AI";
      input: ChatAIUploadInput;
    }
  | {
      app: "IMAGE_AI";
      input: ImageAIUploadInput;
    }
  | {
      app: "ISEARCH";
      input: ISearchUploadInput;
    }
  | {
      app: "TRANSLATE_AI";
      input: TranslateAIUploadInput;
    }
  | {
      app: "TRAINING_AI";
      input: TrainingAiUploadInput;
    }
  | {
      app: "MARKET_RESEARCH";
      input: MarketResearchUploadInput;
    }
  | {
      app: "SHARED";
      input: SharedUploadInput;
    };

// Metadata file type
type Metadata = { uploadUrl?: string; parentObjectId?: string } & FileType;

// Upload statuses
export type UploadStatus =
  | "pending"
  | "creatingMetadata"
  | "metadataCreated"
  | "uploading"
  | "uploadCompleted"
  | "processing"
  | "completed"
  | "removed"
  | "error";

// The upload item used internally and in the components using the functionality
export type UploadItem = {
  status: UploadStatus;
  metadata?: Metadata;
  error?: string;
  type: UploadType;
  file: File;
  data?: Record<string, unknown>;
  key: string;
  groupKey?: string;
  onStatusChange?: (item: UploadItem) => void | Promise<void>;
  onSuccess?: (item: UploadItem) => void | Promise<void>;
  onError?: (item: UploadItem) => void | Promise<void>;
};

// Handler for the upload
export type UploadHandler = {
  type: UploadType;
  createFileMetadata: (item: UploadItem) => Promise<Metadata | undefined>;
  // Resolves to null when the authorized source was removed during finalization.
  updateFileMetadataStatus: (
    item: UploadItem,
    status: "UPLOADED" | "UPLOAD_FAILED",
  ) => Promise<FileType | null>;
  // Poll a file's processing status. Resolves to `null` when the file no longer
  // exists (removed mid-upload or by the unreferenced-file cleanup job) so the
  // poller can stop and drop the item instead of erroring.
  getFileStatus?: (item: UploadItem) => Promise<{
    id: string;
    status: string;
    errorText: string | null;
    processed: boolean;
    workerJobStatus?: WorkerJobStatusEnumType;
  } | null>;
};

// Context
type FileUploaderContextType = {
  items: UploadItem[];
  addItem: (item: UploadItem) => void;
  removeItem: (uploadUrl: string) => void;
  addHandler: (handler: UploadHandler) => void;
};
export const FileUploaderContext = React.createContext<
  FileUploaderContextType | undefined
>(undefined);

// Provider
interface FileUploadProviderProps {
  children: React.ReactNode;
}
export const FileUploadProvider = (props: FileUploadProviderProps) => {
  // Local state
  const [items, setItems] = useState<UploadItem[]>([]);
  const handlersRef = useRef<UploadHandler[]>([]);
  const itemHandlersRef = useRef(new Map<string, UploadHandler>());
  const pollingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Cleanup polling timeouts on unmount
  useEffect(() => {
    const pollingTimeouts = pollingTimeoutsRef.current;

    return () => {
      pollingTimeouts.forEach((timeout) => clearTimeout(timeout));
      pollingTimeouts.clear();
    };
  }, []);

  // Registration must stay stable and render-free as consumers refresh their callbacks.
  const addHandler = useCallback((handler: UploadHandler) => {
    handlersRef.current = [
      ...handlersRef.current.filter(
        (registered) =>
          registered.type.app !== handler.type.app ||
          registered.type.input !== handler.type.input,
      ),
      handler,
    ];
  }, []);

  // Every stage uses the handler captured at enqueue, even after workspace navigation.
  const getHandler = (item: UploadItem) => {
    const handler = itemHandlersRef.current.get(item.key);
    if (!handler) {
      throw new Error(`No handler bound to upload: ${item.key}`);
    }
    return handler;
  };

  // Update item helper
  const updateItem = (itemUpdate: UploadItem) => {
    setItems((prev) =>
      prev.map((item) => {
        // If not item with update or item is removed ignore all incuming updates
        if (item.status === "removed" || itemUpdate.key !== item.key) {
          return item;
        }
        const updatedItem = { ...item, ...itemUpdate };

        // Callbacks on status change
        const hasStatusUpdate = item.status !== itemUpdate.status;
        if (hasStatusUpdate) {
          void updatedItem.onStatusChange?.(updatedItem);
          if (updatedItem.status === "completed") {
            void updatedItem.onSuccess?.(updatedItem);
          } else if (updatedItem.status === "error") {
            void updatedItem.onError?.(updatedItem);
          }
        }
        return updatedItem;
      }),
    );
  };

  // Create metadata - calls handler to create metadata/file instance on backend
  const createMetadata = async (item: UploadItem) => {
    updateItem({ ...item, status: "creatingMetadata" });
    const [metadata, error] = await safeAwait(
      getHandler(item).createFileMetadata(item),
    );
    if (error) {
      updateItem({
        ...item,
        status: "error",
        error: "Metadata creation failed",
      });
    } else {
      updateItem({ ...item, metadata, status: "metadataCreated" });
    }
  };

  // Perform the upload of the file to the blob storage
  const upload = (item: UploadItem) => {
    updateItem({ ...item, status: "uploading" });

    // Check that the item has metadata and upload URL.
    if (!item.metadata || !item.metadata.uploadUrl) {
      logFrontendError(
        new Error("Upload metadata or upload URL missing"),
        "Upload metadata or upload URL missing",
        "SHARED",
        { item },
      );
      updateItem({
        ...item,
        status: "error",
      });
      return;
    }

    void uploadFile({
      file: item.file,
      meta: item.metadata as Metadata & { uploadUrl: string },
      onSuccess: () => {
        updateItem({ ...item, status: "uploadCompleted" });
      },
      onError: () => {
        updateItem({ ...item, status: "error", error: "File upload failed" });
      },
      attachment: !isPreviewFileType(item.metadata),
    });
  };

  // Start polling for file processing status (worker completion)
  const startPolling = (item: UploadItem) => {
    const handler = getHandler(item);
    if (!handler.getFileStatus) {
      // No polling support — mark completed immediately
      updateItem({
        ...item,
        status: "completed",
      });
      return;
    }

    const metadata = item.metadata;
    if (!metadata) {
      updateItem({
        ...item,
        status: "error",
        error: "Upload metadata missing",
      });
      return;
    }

    const clearPolling = () => {
      const timeout = pollingTimeoutsRef.current.get(item.key);
      if (timeout) {
        clearTimeout(timeout);
      }
      pollingTimeoutsRef.current.delete(item.key);
    };

    clearPolling();

    // Polling is scheduled one timeout at a time so each retry can use the
    // latest exponential backoff delay and be cancelled via pollingTimeoutsRef.
    let nextDelayMs = PROCESSING_POLL_INITIAL_DELAY_MS;
    let statusCheckFailedPollCount = 0;

    const scheduleNextPoll = () => {
      if (!pollingTimeoutsRef.current.has(item.key)) {
        return;
      }

      const timeout = setTimeout(() => {
        void poll();
      }, nextDelayMs);
      pollingTimeoutsRef.current.set(item.key, timeout);
      nextDelayMs = Math.min(
        nextDelayMs * PROCESSING_POLL_BACKOFF_FACTOR,
        PROCESSING_POLL_MAX_DELAY_MS,
      );
    };

    const keepPolling = (params?: {
      error?: string;
      errorText?: string | null;
    }) => {
      updateItem({
        ...item,
        metadata: {
          ...metadata,
          errorText: params?.errorText ?? metadata.errorText,
        },
        status: "processing",
        error: params?.error,
      });
      scheduleNextPoll();
    };

    const poll = async () => {
      try {
        const result = await handler.getFileStatus!(item);
        statusCheckFailedPollCount = 0;

        // The file was deleted (the user removed it, or cleanup ran) while we
        // polled its processing status. Stop polling and drop the item instead
        // of erroring or looping until the retry limit.
        if (result === null) {
          clearPolling();
          updateItem({ ...item, status: "removed" });
          return;
        }

        const fileStatus = result.status as FileType["status"];

        if (fileStatus === RECOVERABLE_PROCESSING_FAILED_STATUS) {
          // A PROCESSING_FAILED status can be transient while the worker job is
          // still queued/running, so keep polling until the worker finishes.
          if (
            result.workerJobStatus === "QUEUED" ||
            result.workerJobStatus === "STARTED"
          ) {
            keepPolling({
              error: result.errorText
                ? `${result.errorText}. Retrying...`
                : "File processing failed. Retrying...",
              errorText: result.errorText,
            });
            return;
          }
        }

        if (!SUCCESSFUL_FILE_STATUSES.includes(fileStatus)) {
          clearPolling();
          updateItem({
            ...item,
            metadata: {
              ...metadata,
              status: fileStatus,
              errorText: result.errorText,
            },
            status: "error",
            error: result.errorText || "File processing failed",
          });
          return;
        }

        if (result.processed) {
          // Worker finished processing the file, or the file does not need worker processing.
          clearPolling();
          updateItem({
            ...item,
            metadata: {
              ...metadata,
              status: fileStatus,
              errorText: null,
            },
            status: "completed",
            error: undefined,
          });
          return;
        }

        keepPolling({ errorText: null });
      } catch {
        statusCheckFailedPollCount += 1;

        // Backend/network failures while checking status are retried, but only
        // up to a fixed limit so uploads cannot stay stuck in processing forever.
        if (
          statusCheckFailedPollCount > STATUS_CHECK_FAILED_POLL_MAX_ATTEMPTS
        ) {
          clearPolling();
          updateItem({
            ...item,
            status: "error",
            error: "Failed to check file status",
          });
          return;
        }

        keepPolling({
          error: "Failed to check file status. Retrying...",
        });
      }
    };

    const initialTimeout = setTimeout(() => {
      void poll();
    }, nextDelayMs);
    pollingTimeoutsRef.current.set(item.key, initialTimeout);
    nextDelayMs = Math.min(
      nextDelayMs * PROCESSING_POLL_BACKOFF_FACTOR,
      PROCESSING_POLL_MAX_DELAY_MS,
    );
  };

  // Update metadata - calls handler to update the file instance on backend
  const updateFileMetadata = async (item: UploadItem) => {
    updateItem({ ...item, status: "processing" });
    const handler = getHandler(item);
    const [metadata, error] = await safeAwait(
      handler.updateFileMetadataStatus(item, "UPLOADED"),
    );
    if (error) {
      updateItem({
        ...item,
        status: "error",
        error: "File processing failed",
      });
    } else if (metadata === null) {
      // The source was removed while the server finalized the upload.
      updateItem({ ...item, status: "removed" });
    } else if (
      !SUCCESSFUL_FILE_STATUSES.includes(metadata.status) &&
      (metadata.status !== RECOVERABLE_PROCESSING_FAILED_STATUS ||
        !handler.getFileStatus)
    ) {
      updateItem({
        ...item,
        metadata,
        status: "error",
        error: "File processing failed",
      });
    } else if (handler.getFileStatus) {
      // Handler supports polling — worker was dispatched, wait for completion
      updateItem({
        ...item,
        metadata: { ...item.metadata, ...metadata },
        status: "processing",
      });
      startPolling({ ...item, metadata: { ...item.metadata, ...metadata } });
    } else {
      updateItem({
        ...item,
        metadata: { ...item.metadata, ...metadata },
        status: "completed",
      });
    }
  };

  // Monitor items and process them
  useEffect(
    () => {
      // Polling is registered only after finalization returns and stays registered
      // during status requests. Waiting for a worker must not occupy an upload slot.
      const activeUploads = items.filter(
        (item) =>
          item.status !== "pending" &&
          uploadItemInProgress(item.status) &&
          !pollingTimeoutsRef.current.has(item.key),
      );

      // Count pending items and start max 3 - activeUploads
      const pendingItems = items.filter((item) => item.status === "pending");
      pendingItems
        .slice(0, Math.max(0, MAX_UPLOAD - activeUploads.length))
        .forEach((item) => {
          void createMetadata(item);
        });

      // Process items based on status
      items.forEach((item) => {
        switch (item.status) {
          case "metadataCreated":
            void upload(item);
            break;
          case "uploadCompleted":
            void updateFileMetadata(item);
            break;
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items],
  );

  return (
    <FileUploaderContext.Provider
      value={{
        items,
        addItem: (item: UploadItem) => {
          const handler = handlersRef.current.find(
            (registered) =>
              registered.type.app === item.type.app &&
              registered.type.input === item.type.input,
          );
          if (!handler) {
            throw new Error(
              `No handler found for upload type: ${JSON.stringify(item.type)}`,
            );
          }
          // Bind pending items too: they may not start until after the workspace changes.
          itemHandlersRef.current.set(item.key, handler);
          setItems((prev) => [...prev, item]);
        },
        addHandler,
        removeItem: (key: string) => {
          const item = items.find((i) => i.key === key);
          if (item) {
            const timeout = pollingTimeoutsRef.current.get(key);
            if (timeout) {
              clearTimeout(timeout);
              pollingTimeoutsRef.current.delete(key);
            }
            updateItem({ ...item, status: "removed" });
          }
        },
      }}
    >
      {props.children}
    </FileUploaderContext.Provider>
  );
};

export const prettyStatusLabel = (status: UploadStatus) => {
  switch (status) {
    case "creatingMetadata":
    case "metadataCreated":
    case "uploading":
      return "Uploading";
    case "processing":
      return "Processing";
    default:
      return _.capitalize(status);
  }
};

const progressStatuses = new Set<UploadStatus>([
  "pending",
  "creatingMetadata",
  "metadataCreated",
  "uploading",
  "uploadCompleted",
  "processing",
]);

export const uploadItemInProgress = (status: UploadStatus) => {
  return progressStatuses.has(status);
};

const isPreviewFileType = (file: FileType | null) => {
  if (!file) {
    return false;
  }

  return (
    file.mimeType === PDF_FILE_MIME_TYPE ||
    `.${file.name.split(".").pop()}` ||
    ""
  );
};
